import React, { useMemo, useState } from "react";
import { formatDecimal } from "../utils/formatNumber";

const formatDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("es-AR") : "—";
};

const statusLabel = status => ({
  active: "Vigente",
  scheduled: "Programado",
  expired: "Vencido",
  cancelled: "Cancelado",
}[status] || "Estado no disponible");

const reconciliationStatusLabel = status => ({
  pending: "Pendiente",
  processing: "En proceso",
  completed: "Completada",
  failed: "Con error",
}[status] || "Estado no disponible");

const localIso = value => value ? new Date(`${value}T00:00:00Z`).toISOString() : null;

export const PlanetHdPoolPanel = ({ pools, loading, error, onReload, onCreate, onReconcile }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({
    purchased_ha: "",
    period_start: "",
    period_end: "",
    tier: "",
    country_or_region: "",
    provider_account_id: "",
    provider_contract_id: "",
    product_id: "",
    product_name: "PlanetScope",
    aum_mode: "quota_reservation",
  });
  const orderedPools = useMemo(() => [...(pools || [])].sort(
    (left, right) => new Date(right.period_start) - new Date(left.period_start)
  ), [pools]);

  const change = event => setForm(value => ({ ...value, [event.target.name]: event.target.value }));
  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    const payload = {
      ...form,
      purchased_ha: Number(form.purchased_ha),
      product_id: Number(form.product_id),
      period_start: localIso(form.period_start),
      period_end: localIso(form.period_end),
      idempotency_key: `planet-hd-pool:${form.provider_contract_id}:${form.period_start}:${form.period_end}`,
    };
    const result = await onCreate(payload);
    setSaving(false);
    if (!result?.ok) {
      setFormError(result?.message || "No se pudo registrar el período.");
      return;
    }
    setShowForm(false);
  };

  return (
    <section className="admin-dashboard-resources admin-planet-pools">
      <div className="admin-dashboard-resources__heading">
        <div>
          <h3 className="admin-dashboard-resources__title">Períodos del proveedor Planet HD</h3>
          <p>Registro interno de capacidad ya contratada. Esta pantalla no compra ni amplía contratos en Planet.</p>
        </div>
        <button type="button" className="btn btn-light-primary" onClick={() => setShowForm(value => !value)}>
          {showForm ? "Cancelar" : "Registrar próximo período"}
        </button>
      </div>

      {error ? (
        <div className="admin-resource-summary__notice">
          <span>No se pudieron cargar los períodos de Planet HD.</span>
          <button type="button" className="btn btn-link p-0" onClick={onReload}>Reintentar</button>
        </div>
      ) : loading ? (
        <div className="admin-planet-hd__notice">Cargando períodos…</div>
      ) : (
        <div className="admin-planet-pools__grid">
          {orderedPools.map(pool => (
            <article key={pool.pool_id} className="admin-planet-pool-card">
              <div className="admin-planet-pool-card__header">
                <strong>{statusLabel(pool.status)}</strong>
                <span>{formatDate(pool.period_start)} – {formatDate(pool.period_end)}</span>
              </div>
              <dl>
                <div><dt>Comprada</dt><dd>{formatDecimal(pool.purchased_ha)} ha</dd></div>
                <div><dt>Comprometida</dt><dd>{formatDecimal(pool.committed_ha)} ha</dd></div>
                <div><dt>Habilitada</dt><dd>{formatDecimal(pool.covered_ha)} ha</dd></div>
                <div><dt>En proceso</dt><dd>{formatDecimal(pool.reserved_ha)} ha</dd></div>
                <div><dt>Disponible</dt><dd>{formatDecimal(pool.remaining_assignable_ha)} ha</dd></div>
              </dl>
              {pool.rollover?.status && (
                <div className={`admin-planet-pool-card__rollover admin-planet-pool-card__rollover--${pool.rollover.status}`}>
                  <span>Reconciliación: {reconciliationStatusLabel(pool.rollover.status)}</span>
                  <small>{pool.rollover.succeeded || 0} correctas / {pool.rollover.failed || 0} fallidas</small>
                  {["pending", "failed"].includes(pool.rollover.status) && (
                    <button type="button" className="btn btn-sm btn-light-primary" onClick={() => onReconcile(pool.pool_id)}>
                      Reintentar
                    </button>
                  )}
                </div>
              )}
            </article>
          ))}
          {!orderedPools.length && <div className="admin-planet-hd__notice">No hay períodos registrados.</div>}
        </div>
      )}

      {showForm && (
        <form className="admin-planet-pool-form" onSubmit={submit}>
          <h4>Registrar capacidad ya contratada</h4>
          {[
            ["purchased_ha", "Capacidad comprada (ha)", "number"],
            ["period_start", "Inicio", "date"],
            ["period_end", "Fin", "date"],
            ["provider_account_id", "Cuenta del proveedor", "text"],
            ["provider_contract_id", "Contrato del proveedor", "text"],
            ["product_id", "ID del producto", "number"],
            ["product_name", "Nombre del producto", "text"],
            ["tier", "Tier", "text"],
            ["country_or_region", "País o región", "text"],
          ].map(([name, label, type]) => (
            <label key={name}>{label}<input className="form-control" name={name} type={type} value={form[name]} onChange={change} required={!['tier', 'country_or_region'].includes(name)} /></label>
          ))}
          <label>Modo AUM<select className="form-control" name="aum_mode" value={form.aum_mode} onChange={change}><option value="quota_reservation">Reserva de cuota</option><option value="legacy_embedded_aum">AUM heredado</option></select></label>
          {formError && <div className="admin-planet-hd__error">{formError}</div>}
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Registrando…" : "Registrar período"}</button>
        </form>
      )}
    </section>
  );
};
